import React, { useState } from "react"
import PageHeader from "components/Ui/Page/PageHeader"
import { useMutation, useQuery } from "@apollo/client"
import PrimaryButton from "components/Ui/Buttons/PrimaryButton"

import { SubmitHandler, useForm } from "react-hook-form"
import alertActions from "lib/store/services/Alert/AlertSlice"
import { useAppDispatch } from "lib/store"
import { v4 as uuidv4 } from "uuid"

import {
  CampaignGroup,
  InputCampaign,
  InputStoreMarket,
  InputMarketRoundingRule,
  InputPriceRule,
  InputPriceRuleType,
  InputRoundingRules,
  StoreGroup
} from "lib/types/generated/graphql-types"
import CREATE_OR_UPDATE_CAMPAIGN from "graphql/mutations/campaign/CreateOrUpdateCampaign"
import General from "../../components/Campaign/General/General"
import { ButtonWrapper, IconLeft, IconRight } from "./CreateCampaign.styled"
import { CampaignStoreMarket } from "../../components/Campaign/StoreMarket/CampaignStoreMarket"
import SecondaryButton from "../../components/Ui/Buttons/SecondaryButton"
import ProductVariantRules from "../../components/Campaign/ProductVariantRules/ProductVariantRules"
import { RoundingRules } from "../../components/Campaign/RoundingRules/RoundingRules"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import { CampaignTagRule } from "lib/types/campaign"
import PriceRule from "../../components/Campaign/PriceRule/PriceRule"
import ALL_STORE_GROUPS from "../../graphql/queries/store/AllStoreGroups"
import SEARCH_CAMPAIGN_GROUPS from "../../graphql/queries/campaign/SearchCampaignGroups"
import { useNavigate } from "react-router-dom"
import ProductPreview from "../../components/Campaign/ProductVariantRules/ProductPreview/ProductPreview"
import CampaignTimeLine from "../../components/Campaign/TimeLine/CampaignTimeLine"

enum Step {
  VARIANT_RULES,
  STORE_MARKETS,
  PRICE_RULES,
  ROUNDING_RULES,
  GENERAL
}

export const CreateCampaign = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [storeGroups, setStoreGroups] = useState<StoreGroup[]>()
  const [tagRules, setTagRules] = useState<CampaignTagRule[]>([{ tagConditions: [] }])
  const [addedStoreMarkets, setAddedStoreMarkets] = useState<InputStoreMarket[]>([])
  const [marketRules, setMarketRules] = useState<InputMarketRoundingRule[]>([])
  const [priceRule, setPriceRule] = useState<InputPriceRule>({
    type: InputPriceRuleType.RelativeDiscount
  })
  const [roundingRules, setRoundingRules] = useState<InputRoundingRules>()
  const [step, setStep] = useState<Step>(Step.VARIANT_RULES)
  const [campaignGroups, setCampaignGroups] = useState<CampaignGroup[]>([])

  useQuery(SEARCH_CAMPAIGN_GROUPS, {
    variables: {
      from: 0,
      size: 40,
      sort: [{ field: "updated", order: "DESC" }]
    },
    onCompleted: (data) => {
      setCampaignGroups(data?.searchCampaignGroups?.hits ?? [])
    }
  })

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    getValues,
    watch
  } = useForm<InputCampaign>()

  const startDate = watch("startDate")
  const endDate = watch("endDate")
  const campaignName = watch("name")

  useQuery(ALL_STORE_GROUPS, {
    onCompleted: (data) => {
      setStoreGroups(data.getStoreGroups)
    }
  })

  const [addCampaign, { loading: createLoading }] = useMutation(CREATE_OR_UPDATE_CAMPAIGN, {
    onCompleted: (data) => {
      dispatch(
        alertActions.actions.createAlert({
          message: "Campaign successfully created",
          type: "success"
        })
      )
      navigate(`/campaigns/campaign/${data.createOrUpdateCampaign.id}`)
    },
    onError: (error) => {
      dispatch(
        alertActions.actions.createAlert({
          message: error.message,
          type: "error"
        })
      )
    }
  })

  const getTagLogicJSON = () => {
    const transformed = {
      or: tagRules.map((tagRule) => ({
        and: tagRule.tagConditions.map((tagCondition) => ({
          ...(tagCondition.include
            ? {
                contains: {
                  key: tagCondition.key,
                  values: tagCondition.values
                }
              }
            : {
                ["!"]: {
                  contains: {
                    key: tagCondition.key,
                    values: tagCondition.values
                  }
                }
              })
        }))
      }))
    }
    return JSON.stringify(transformed)
  }

  const onSubmit: SubmitHandler<InputCampaign> = (data) => {
    addCampaign({
      variables: {
        campaignGroupId: data.campaignGroupId,
        name: data.name,
        id: uuidv4(),
        priceRule: priceRule,
        storeMarkets: addedStoreMarkets,
        startDate: new Date(data.startDate),
        endDate: new Date(data.endDate),
        ...(roundingRules?.defaultRule.targets
          ? {
              roundingRules: {
                defaultRule: roundingRules?.defaultRule,
                marketRules: roundingRules?.marketRules ?? []
              }
            }
          : { roundingRules: null }),
        productVariantRules: {
          tagLogic: getTagLogicJSON()
        }
      }
    })
  }

  dayjs.extend(utc)
  dayjs.extend(timezone)
  const getOperatorCheck = () => {
    if (priceRule.type === InputPriceRuleType.RelativeDiscount) {
      return (
        !priceRule.percentage ||
        priceRule.percentageDecimals === null ||
        priceRule.percentageDecimals === undefined ||
        isNaN(priceRule.percentageDecimals)
      )
    } else {
      return !priceRule.monies || priceRule.monies.length === 0
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <PageHeader
        title={"Create campaign"}
        group="Campaigns"
        hasTabs
        goBackLinkUrl="/campaigns"
        goBackLinkText="Back to campaigns"
      ></PageHeader>
      {step === Step.VARIANT_RULES && (
        <>
          <ProductVariantRules tagRules={tagRules} setTagRules={setTagRules} />
          <ButtonWrapper>
            <PrimaryButton
              disabled={tagRules[0]?.tagConditions?.length === 0}
              handleClick={() => {
                setStep(Step.STORE_MARKETS)
              }}
            >
              Store markets
              <IconRight />
            </PrimaryButton>
          </ButtonWrapper>
          {tagRules[0]?.tagConditions?.length > 0 && <h2>Product variant preview</h2>}
          {tagRules.map((tagRule, index) => (
            <ProductPreview key={uuidv4()} tagRule={tagRule} index={index} />
          ))}
        </>
      )}
      {step === Step.STORE_MARKETS && (
        <>
          <CampaignStoreMarket
            addedStoreMarkets={addedStoreMarkets}
            setAddedStoreMarkets={setAddedStoreMarkets}
            storeGroups={storeGroups}
          />
          <ButtonWrapper>
            <SecondaryButton
              handleClick={() => {
                setStep(Step.VARIANT_RULES)
              }}
            >
              <IconLeft />
              Back
            </SecondaryButton>
            <PrimaryButton
              disabled={addedStoreMarkets.length === 0}
              handleClick={() => {
                setStep(Step.PRICE_RULES)
              }}
            >
              Price rules
              <IconRight />
            </PrimaryButton>
          </ButtonWrapper>
        </>
      )}
      {step === Step.PRICE_RULES && (
        <>
          <PriceRule
            priceRule={priceRule}
            setPriceRule={setPriceRule}
            addedStoreMarkets={addedStoreMarkets}
            storeGroups={storeGroups}
          />

          <ButtonWrapper>
            <SecondaryButton
              handleClick={() => {
                setStep(Step.STORE_MARKETS)
              }}
            >
              <IconLeft />
              Back
            </SecondaryButton>
            <PrimaryButton
              disabled={getOperatorCheck()}
              handleClick={() => {
                if (priceRule.type !== InputPriceRuleType.RelativeDiscount) {
                  setStep(Step.GENERAL)
                } else {
                  setStep(Step.ROUNDING_RULES)
                }
              }}
            >
              {priceRule.type !== InputPriceRuleType.RelativeDiscount
                ? "General"
                : "Rounding rules"}
              <IconRight />
            </PrimaryButton>
          </ButtonWrapper>
        </>
      )}
      {step === Step.ROUNDING_RULES && (
        <>
          <RoundingRules
            marketRules={marketRules}
            setMarketRules={setMarketRules}
            addedStoreMarkets={addedStoreMarkets}
            roundingRules={roundingRules}
            setRoundingRules={setRoundingRules}
          />
          <ButtonWrapper>
            <SecondaryButton
              handleClick={() => {
                setStep(Step.PRICE_RULES)
              }}
            >
              <IconLeft />
              Back
            </SecondaryButton>
            <PrimaryButton
              handleClick={() => {
                setStep(Step.GENERAL)
              }}
            >
              General
              <IconRight />
            </PrimaryButton>
          </ButtonWrapper>
        </>
      )}
      {step === Step.GENERAL && (
        <>
          <General
            control={control}
            errors={errors}
            setValue={setValue}
            campaignGroups={campaignGroups}
            setCampaignGroups={setCampaignGroups}
            getValues={getValues}
            startDateValidation={true}
          />
          <ButtonWrapper>
            <SecondaryButton
              handleClick={() => {
                if (priceRule.type !== InputPriceRuleType.RelativeDiscount) {
                  setStep(Step.PRICE_RULES)
                } else {
                  setStep(Step.ROUNDING_RULES)
                }
              }}
            >
              <IconLeft />
              Back
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              handleClick={handleSubmit(onSubmit)}
              loading={createLoading}
              disabled={createLoading}
            >
              Save
            </PrimaryButton>
          </ButtonWrapper>
          {startDate && endDate && (
            <CampaignTimeLine
              previewCampaign={{
                name: campaignName || "New Campaign",
                startDate: startDate,
                endDate: endDate
              }}
            />
          )}
        </>
      )}
    </form>
  )
}
